import _ from '@sparta.global.vendors/lodash';
import renderModuleTemplate from '@sparta.global.templates/render-module-template/dist/es6.js';
import spartaContentManagerUtility from '@sparta.global.utilities/sparta-content-manager-utility/dist/es6.js';
import spartaStoreUtility from '@sparta.global.utilities/sparta-store-utility/dist/es6.js';
import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import spartaMediaQueryUtility from '@sparta.global.utilities/sparta-mediaquery-utility/dist/es6.js';
import spartaTransportUtility from '@sparta.global.utilities/sparta-transport-utility/dist/es6.js';
import spartaUILayersUtility from '@sparta.global.utilities/sparta-ui-layers-utility/dist/es6.js';
import spartaGlobalFunctions from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';
import '@sparta.global.utilities/sparta-browser-support-utility/dist/es6.js';

/**
 * Validate and retrieve the target element
 * @param {string|HTMLElement} target Target element
 * @returns {HTMLElement} Validated target element
 */
function getMountElement(target) {
  const element = target instanceof HTMLElement ? target : document.querySelector(target);
  if (!(element instanceof HTMLElement)) {
    throw new Error(`Target ${target} isn't a valid HTML element or query selector string.`);
  }
  return element;
}

/**
 * Verify and create a new module Id if required
 * @param {string} module Id
 * @returns {string} same or new module Id
 */
function getModuleUniqueId(moduleId) {
  if (!document.getElementById(moduleId)) {
    return moduleId;
  }
  let moduleCount = 0;
  while (document.getElementById(moduleId + ++moduleCount));
  return moduleId + moduleCount;
}

/**
 * Method to fetch the webcontent using SCMU, from store as well as endpoint service.
 * @param {string} contentKeys web content keys.
 * @returns {object} web content response object.
 */
async function getContent(contentKeys) {
  const response = await spartaContentManagerUtility.getContent({
    key: contentKeys,
    webContent: true
  }, true);
  return response;
}

/**
 * Method to extract webcontent keys from runtime modules object.
 * @param {object} runtimeModules an object with runtime modules and its data sources details.
 * @returns {array} web content keys.
 */
function extractWebContentKeys(runtimeModules) {
  const webContentKeys = {};
  runtimeModules.forEach(module => {
    if (module.dataSources && typeof module.dataSources === 'object') {
      Object.values(module.dataSources).forEach(value => {
        if (typeof value === 'string' && value.startsWith('/WEB_CONTENT') && !webContentKeys[value]) {
          webContentKeys[value] = value.replace('/WEB_CONTENT/', '').trim();
        }
      });
    }
  });
  return webContentKeys;
}

/**
 * Method to fetch the webcontent on page load.
 * @param {object} runtimeModules an object with runtime modules and its data sources details.
 * @returns {object} web content response object.
 */
async function fetchRuntimeModulesContents(runtimeModules) {
  const webContentKeys = extractWebContentKeys(runtimeModules);
  const webContents = await getContent(Object.keys(webContentKeys));
  return webContents;
}

/**
 * Loads on-demand data sources asynchronously and replaces data source reference with loaded data object
 */
async function loadDataSourceAsync(jcrReference) {
  // Skip download if jcrReference is already an object
  if (typeof jcrReference === "string") {
    if (jcrReference.includes('WEB_CONTENT')) {
      const content = await getContent(jcrReference);
      return content;
    } else {
      // const onDemandJcrLoader = window.dynamicImportMap.jcrs.onDemand[jcrReference]
      const spaParams = spartaStoreUtility.getData("spaParams");
      const dynamicImport = spaParams["dynamicImportMap"] || {};
      const runtimeModules = spaParams['runtimeModules'] || {};
      const onDemandModulesJcrs = [...(dynamicImport.jcrs || []), ...(runtimeModules.jcrs || [])];
      let jcrEntry;
      if (onDemandModulesJcrs) {
        jcrEntry = onDemandModulesJcrs.find(a => a.name === jcrReference.replace('{language}', spaParams.language));
      }
      if (jcrEntry === undefined) {
        throw new Error(`ON-DEMAND JCR NOT FOUND: ${jcrReference}`);
      }
      const loadedData = (await jcrEntry.loader()).default;
      // dataSources[dataSourceName] = loadedData.default
      const processedData = jcrEntry.name.endsWith('.md') ? loadedData : await spartaContentManagerUtility.processWebContents(loadedData);
      return processedData;
    }
  }
  if (typeof jcrReference === "object") {
    // Return the jcrReference as is since it has already an object
    const processedData = await spartaContentManagerUtility.processWebContents(jcrReference);
    return processedData;
  }
  throw new Error(`ON-DEMAND JCR REFERENCE UNKNOWN: ${jcrReference}`);
}
async function resolveModulesDataSources(dataSourcesMap) {
  dataSourcesMap = dataSourcesMap || {};
  const dataSourceKeys = Object.keys(dataSourcesMap);
  const dataSourcePromiseArray = [];
  const spaParams = spartaStoreUtility.getData("spaParams");
  dataSourceKeys.forEach(key => {
    const {
      language
    } = spaParams;
    const jcrReference = dataSourcesMap[key][language] || dataSourcesMap[key];
    dataSourcePromiseArray.push(loadDataSourceAsync(jcrReference));
  });

  // Wait for all the data sources to load
  const dataSourceResults = await Promise.all(dataSourcePromiseArray);
  dataSourceKeys.forEach((key, index) => {
    dataSourcesMap[key] = dataSourceResults[index];
  });
}
async function createModuleRenderContext(context, moduleConfig, childModules = null, id = null, target = null) {
  await resolveModulesDataSources(context.dataSources);
  const uniqueId = id ? id : getModuleUniqueId(moduleConfig.id);
  const moduleRenderContext = {
    config: {
      ...moduleConfig,
      id: uniqueId,
      webContentTarget: target
    },
    params: {
      ...context.params
    },
    ...context.params,
    dataSources: context.dataSources,
    childModules
  };

  // Populate params with default and overridden entries
  moduleConfig.moduleParams.map(param => {
    moduleRenderContext.params[param.name] = moduleRenderContext.params[param.name] !== undefined ? moduleRenderContext.params[param.name] : param.default ? param.default : "";
  });

  // Populate params with selected module config properties
  const paramsConfigProperties = ["moduleName", "version", "moduleParams", "viewPort", "id", "nameSpace", "tag", "components", "semantic"];
  paramsConfigProperties.map(propertyName => {
    if (moduleConfig[propertyName]) {
      moduleRenderContext.params[propertyName] = moduleConfig[propertyName];
    }
  });
  moduleRenderContext.name = moduleConfig.name;
  return moduleRenderContext;
}

/**
 * Mounts a set of module objects (or single object) to the given selectors/elements.
 *
 * Schema:
 * (required) module: reference of the module to mount
 * (required) target: string (or HTMLElement) of the element to mount the module to
 * (optional) context: object of dataSources and/or params to compile the module with.
 * dataSource values can either be relative-pathed URLs or objects, and any URLs provided will be
 * fully resolved as objects.
 * params values must be fully resolved.
 *
 * Advanced:
 * (optional) version: hardcoded version of the module to load, such as '1.0.0'.
 *
 *
 * Usage #1: module is an Array of modules to mount
 * this.mountAll([{
 *   module: 'sparta-demo/global-nav-module',
 *   target: '#some-selector',
 *   context: {
 *     dataSources: {
 *       data1: '/sparta-demo/some/path/file.json'
 *     },
 *     params: {
 *       foo: 'bar'
 *     }
 *   }
 * }]);
 *
 * Usage #2: module is a single object that represents a single module to mount
 * this.mountAll({
 *   module: 'sparta-demo/global-nav-module',
 *   target: '#some-selector',
 *   context: {
 *     dataSources: {
 *       data1: '/sparta-demo/some/path/file.json'
 *     },
 *     params: {
 *       foo: 'bar'
 *     }
 *   }
 * });
 *
 * @param {Array|Object} module An array (or single object) of modules to load.
 * @memberof Module
 * @returns {Promise}
 */
async function mountAll(modules, widgetInfo = [], renderRuntimeModules = false) {
  const failedModules = [];
  const moduleArray = Array.isArray(modules) ? modules : [modules];
  const spaParams = spartaStoreUtility.getData("spaParams");
  let onDemandModules = null;
  if (renderRuntimeModules) {
    const runtimeModules = spaParams['runtimeModules'];
    onDemandModules = [...runtimeModules.modules];
    if (!spaParams.moduleLevelCMSRequest) await fetchRuntimeModulesContents(runtimeModules.modules);
  } else {
    const dynamicImports = spaParams["dynamicImportMap"];
    onDemandModules = dynamicImports.modules;
  }
  for (const moduleDef of moduleArray) {
    const {
      module,
      target,
      context,
      childModules,
      id
    } = moduleDef;
    let moduleEntry;
    if (onDemandModules) {
      moduleEntry = onDemandModules.find(a => a.name === module);
      if (spaParams.moduleLevelCMSRequest && renderRuntimeModules) {
        const childModuleNames = childModules.map(module => module.name);
        const runtimeModules = onDemandModules.filter(onDemandModule => [module, ...childModuleNames].includes(onDemandModule.name));
        await fetchRuntimeModulesContents(runtimeModules);
      }
    }
    if (moduleEntry === undefined) {
      throw new Error(`ERROR - ON-DEMAND MODULE NOT FOUND: ${module}`);
    }

    // Execute dynamic import
    const [loadedOnDemandModuleData] = await moduleEntry.loader();
    // Verify component loaded successfully
    if (!loadedOnDemandModuleData) {
      throw new Error(`ERROR - ON-DEMAND MODULE FAILED TO LOAD: ${module}`);
    }
    const {
      moduleConfig
    } = loadedOnDemandModuleData;

    // Process child modules
    if (childModules && childModules.length) {
      for (const childModule of childModules) {
        const childModuleName = childModule.module;
        const childModuleContext = childModule.context;
        const childModuleEntry = onDemandModules.find(a => a.name === childModuleName);
        if (childModuleEntry === undefined) {
          throw new Error(`ERROR - ON-DEMAND MODULE'S CHILD MODULE NOT FOUND: Module - ${module}, CHILD MODULE - ${childModuleName}`);
        }
        const [childModuleData] = await childModuleEntry.loader();
        if (!childModuleData) {
          throw new Error(`ERROR - ON-DEMAND MODULE'S CHILD MODULE FAILED TO LOAD:  Module - ${module}, CHILD MODULE - ${childModuleName}`);
        }
        const childModuleConfig = childModuleData.moduleConfig;
        // Creare child module render context
        Object.assign(childModule, await createModuleRenderContext(childModuleContext, childModuleConfig));

        // Register Child module class
        this.registerModuleClass({
          moduleClass: childModuleData.default,
          moduleConfig: childModuleConfig
        });
      }
    }

    // Create parent module render context
    const moduleRenderContext = await createModuleRenderContext(context, moduleConfig, childModules, id, target);
    if (moduleRenderContext.dataSources) {
      Object.keys(moduleRenderContext.dataSources).forEach(key => {
        const data = moduleRenderContext.dataSources[key];
        if (data?.failed) {
          moduleRenderContext.webContentFailed = true;
        }
      });
    }
    const moduleRenderData = {
      env: spaParams.siteParams.env,
      site: {
        global: spaParams.global,
        params: spaParams.siteParams
      },
      page: {
        parameters: spaParams.pageParams
      },
      language: spaParams.language,
      assetPath: "",
      module: moduleRenderContext,
      partials: moduleConfig.partials,
      helpers: moduleConfig.helpers
    };
    if (!moduleRenderContext.webContentFailed) {
      const html = renderModuleTemplate.render(moduleRenderData);

      // Inject the rendered HTML into the DOM
      if (!moduleEntry.isWebChild) {
        const element = getMountElement(renderRuntimeModules ? `[data-target-id="${target}"]` : target);
        if (renderRuntimeModules) {
          const template = document.createElement('template');
          template.innerHTML = html.trim();
          element.replaceWith(template.content.firstChild);
        } else {
          element.innerHTML += html;
        }
        this.addPreventDefaultForLinks(element);
      }
    } else {
      failedModules.push(moduleRenderData);
    }
    const moduleClass = loadedOnDemandModuleData.default;
    this.registerModuleClass({
      moduleClass,
      moduleConfig
    });
  }
  await this.createAndInitModules(widgetInfo, failedModules);
}

// Third party imports

// Constants

console.log("####################### sparta-bootstrap-utility loaded ##################");
const EVENTS = {
  SPARTA_GLOBAL_READY: "SPARTA_GLOBAL_READY.spartaBootstrapUtility",
  SPARTA_SECONDARY_READY: "SPARTA_SECONDARY_READY.spartaBootstrapUtility",
  SPARTA_WINDOW_SCROLL: "SPARTA_WINDOW_SCROLL.spartaBootstrapUtility"
};
function publishGlobalReadyEvents(loop, events, widgetInfo) {
  if (loop.total === 1) {
    spartaEventsUtility.pub(events.SPARTA_GLOBAL_READY, null, widgetInfo);
  }
  if (loop.total === 2) {
    if (loop.completed === 0) {
      spartaEventsUtility.pub(events.SPARTA_GLOBAL_READY, null, widgetInfo);
    } else if (loop.completed > 0) {
      spartaEventsUtility.pub(events.SPARTA_SECONDARY_READY, null, widgetInfo);
    }
  }
}
class SpartaBootstrapUtility {
  constructor() {
    this.modulesLoaded = {};
    this.modules = [];
    this.initedModules = [];
    this.moduleInstances = [];
    this.appContainer = null;
    this.browser = spartaStoreUtility.getData("browser");
    this.loop = {
      total: 1,
      completed: 0
    };
  }
  get EVENTS() {
    return EVENTS;
  }

  /**
   * Registers the shared component
   * @param {Array} sharedComponents The shared components listed in the dynamic import map
   * @param {int} vueVersion The Vue major version being used
   */
  async registerSharedComponents(sharedComponents) {
    await Promise.all(sharedComponents.map(async comp => {
      // Load and register Shared Components
      const loadedComponent = await comp.loader();
      const componentName = loadedComponent.default ? loadedComponent.default.name : loadedComponent.name;
      // ToDo: Will revisit this later when we a strategy to handle multi exported components from a single package
      // if (!componentName) {
      //   console.warn(`SpartaBootstrapUtility:: Component "${comp.name}" is missing "name" in its definition. It is a good practice to define the name for every Vue component which will help us identify any name conflicts on the page.`);
      // } else {
      //   (componentNameMap[componentName] = componentNameMap[componentName] || []).push(comp.name);
      // }
      if (this.vueVersion === 2) {
        await this.getVue();
        if (componentName && this.vue) {
          if (!this.vue.options.components[componentName]) {
            this.vue.component(componentName, loadedComponent.default || loadedComponent);
          }
        }
      }
      // else if (componentName && this.app) {
      //   if (!this.app._context.components[componentName]) {
      //     this.app.component(componentName, loadedComponent.default || loadedComponent);
      //   }
      // }
    }));

    // for (const [componentName, namespace] of Object.entries(componentNameMap)) {
    //   if (namespace.length > 1) {
    //     console.error(`SpartaBootstrapUtility:: Multiple components are identified with same name "${componentName}" coming from [${namespace.join(', ')}]`);
    //   }
    // }
  }
  /**
   * Registers the store module and publishes the store event
   * @param {Array} stores The shared stores listed in the dynamic import map
   * @param {object} widgetInfo Object for the module widget
   */
  async registerPageStores(stores, widgetInfo) {
    if (stores.length > 0) {
      const [store, vuex] = await Promise.all([this.getVuexStore(), this.getVuex()]);
      this.vuex = vuex;
      this.vuexStore = store;
      for (const storeEntry of stores) {
        const {
          namespace,
          loader
        } = storeEntry;
        if (store.hasModule(namespace) === false) {
          const loadedStore = await loader();
          // Register store module and ensure it is namespaced
          store.registerModule(namespace, {
            ...loadedStore.default,
            namespaced: true
          });
          spartaEventsUtility.pub(`${namespace}_STORE_INITIALIZED`, store, widgetInfo);
        }
      }
    }
  }

  /**
   * Asynchronous function to create and mount the Vue 3 app instance
   * @param {bbject} compDef The Vue component definition
   * @param {string} placeholder Placeholder where the app will be mounted
   * @param {object} nativeRouter Vue router object
   * @returns {object} Vue package export
   */
  async createAndMountApp(compDef, placeholder, nativeRouter) {
    await this.getVue();
    this.app = this.vue.createApp(compDef);

    // turn Vue devtools on in Prod
    if (!this.spaParams.siteParams.isProd) {
      const {
        version
      } = this.app;
      const devtools = window.__VUE_DEVTOOLS_GLOBAL_HOOK__;
      if (devtools && version) {
        devtools.enabled = true;
        devtools.emit('app:init', this.app, version, {});
      }
    }
    const dynamicImport = this.spaParams["dynamicImportMap"];

    // Register shared components
    if (dynamicImport && dynamicImport.sharedComponents) {
      await this.registerSharedComponents(dynamicImport.sharedComponents);
    }
    if (this.vuexStore) {
      this.app.use(this.vuexStore);
      this.app.vuexStore = this.vuexStore;
    }
    if (this.routerInstance) {
      this.app.use(this.routerInstance);
      this.app.router = this.routerInstance;
    }
    if (nativeRouter) {
      this.app.use(nativeRouter);
    }
    this.app.mount(placeholder);
    return this.app;
  }

  /**
   * @param {object} widgetInfo
   * bootstrap kickoff
   */
  async init(widgetInfo) {
    console.log("init bootstrap");
    this.spaParams = this.setupSpaParams(widgetInfo);
    this.setApplicationData(widgetInfo);
    this.loadOptionsFromContainer(widgetInfo);
    this.checkForLangOverride(widgetInfo);
    this.setupGlobalEvents(widgetInfo);
    const dynamicImport = this.spaParams["dynamicImportMap"];
    this.vueVersion = this.spaParams.siteParams.vueMajorVersion || 2;
    if (dynamicImport) {
      // Register shared components
      if (dynamicImport.sharedComponents && this.vueVersion === 2) {
        await this.registerSharedComponents(dynamicImport.sharedComponents);
      }
      // Register and publish stores
      if (dynamicImport.stores) {
        await this.registerPageStores(dynamicImport.stores, widgetInfo);
      }
    }
    await this.createAndInitModules(widgetInfo).then(async () => {
      if (!this.spaParams.isSPA || this.spaParams.isSPA === 'false' || this.hasRouterConfig) {
        publishGlobalReadyEvents(this.loop, this.EVENTS, widgetInfo);
      } else {
        const vueRouterImport = _.find(dynamicImport.modules, module => module.name === '@sparta.global.vendors/vue-router');
        if (vueRouterImport) {
          this.VueRouter = this.vueVersion === 2 ? (await vueRouterImport.loader()).default : await vueRouterImport.loader();
          if (this.vueVersion === 2) {
            await this.getVue();
            this.vue.use(this.VueRouter);
          }
        }
        this.initializeRouter(widgetInfo).then(() => publishGlobalReadyEvents(this.loop, this.EVENTS, widgetInfo));
      }
      this.addPreventDefaultForLinks(document);
    })
    // Ready sparta content manager
    .then(() => {
      this.initContentManagerUtility(widgetInfo);
    });
    await this.mountWebModules(widgetInfo);
  }

  /**
   * Method to mount runtime modules
   * @param {object} widgetInfo
   */
  async mountWebModules(widgetInfo) {
    const webModules = this.spaParams?.runtimeModules?.modules;
    if (webModules && webModules.length > 0) {
      const modulesToMount = [];
      webModules.map(moduleObj => {
        modulesToMount.push({
          module: moduleObj.name,
          id: moduleObj.isWebChild ? moduleObj.id : moduleObj.targetId,
          target: `${moduleObj.targetId}`,
          context: {
            dataSources: moduleObj.dataSources
          },
          childModules: moduleObj.childModules || []
        });
      });
      await this.mountAll(modulesToMount, widgetInfo, true);
      spartaEventsUtility.pub('FOOTNOTES_REBUILD.footnoteUtility');
    }
  }

  /**
   * Asynchronous function to dynamically import Vue package only when needed
   * @returns {object} Vue package export
   */
  getVue = async () => {
    if (this.vue === undefined) {
      const vueImport = _.find(this.spaParams.dynamicImportMap.modules, module => module.name === '@sparta.global.vendors/vue');
      if (vueImport) {
        this.vue = this.vueVersion === 2 ? (await vueImport.loader()).default : await vueImport.loader();
      }
    }
    return this.vue;
  };

  /**
   * Asynchronous function to dynamically import Vuex package only when needed
   * @returns {object} Vuex package export
   */
  getVuex = async () => {
    if (this.vuex === undefined) {
      const vuexImport = _.find(this.spaParams.dynamicImportMap.modules, module => module.name === '@sparta.global.vendors/vuex');
      if (vuexImport) {
        this.vuex = await vuexImport.loader();
        if (this.vueVersion === 2) {
          const Vue = await this.getVue();
          Vue.use(this.vuex);
        }
      }
    }
    return this.vuex;
  };

  /**
   * Asynchronous function to return the Vuex master store
   * @returns {object} Vuex store object
   */
  getVuexStore = async () => {
    if (this.vuexStore === undefined) {
      const Vuex = await this.getVuex();
      this.vuexStore = this.vueVersion === 2 ? new Vuex.Store() : Vuex.createStore({});
    }
    return this.vuexStore;
  };
  setLoop(loopObj) {
    this.loop = loopObj;
  }
  isWidget(widgetInfo) {
    return widgetInfo && widgetInfo.name && widgetInfo.version;
  }
  setupSpaParams(widgetInfo) {
    const httpRegExp = new RegExp('^http(s?)\:', 'i');
    const isWidget = this.isWidget(widgetInfo);
    const spaParams = isWidget ? window.spaWidgetParams[widgetInfo.name][widgetInfo.version][widgetInfo.id] : window.spaParams;
    const protocol = spaParams.siteParams.domain.ssl ? 'https://' : 'http://';
    let isHttpURL = '';
    if (isWidget) {
      spaParams.isWidget = true;
      isHttpURL = httpRegExp.test(spaParams.siteParams.widgetAPIEndpoint);
      spaParams.siteParams.widgetAPIEndpoint = spaParams.siteParams.widgetAPIEndpoint || widgetInfo.widgetAPIEndpoint;
      if (!isHttpURL) {
        spaParams.siteParams.widgetAPIEndpoint = protocol + spaParams.siteParams.widgetAPIEndpoint;
      }
    } else {
      isHttpURL = httpRegExp.test(spaParams.siteParams.domain.hostname);
      if (!isHttpURL) {
        spaParams.siteParams.domain.hostname = protocol + spaParams.siteParams.domain.hostname;
      }
    }

    // set siteParams on spaParams if loading as a widget
    spaParams.siteParams = spaParams.siteParams || spaParams.params;
    const lang = isWidget ? spartaStoreUtility.getData("lang").split("-")[0] : sparta.language;
    const language = lang || "en";
    spaParams.language = language;
    spartaStoreUtility.addData("spaParams", spaParams);

    // update bank holidays based on the current language
    const bankHolidays = spaParams.global[spaParams.language] ? spaParams.global[spaParams.language].bankHolidays : null;
    if (bankHolidays) {
      spartaStoreUtility.addData("bankHolidays", bankHolidays);
    }
    spartaEventsUtility.pub(spartaEventsUtility.EVENTS.SPA_PARAMS_LOADED, spaParams);
    return spaParams;
  }
  setApplicationData(widgetInfo) {
    let appContainer;
    let mediaQueryContainer;
    const isWidget = this.isWidget(widgetInfo);
    spartaStoreUtility.addData("isWidget", isWidget); //Do not pass widgetInfo here because its not required for this flag

    if (isWidget) {
      appContainer = document.querySelector(`#${widgetInfo.id} [data-sparta-container]`);
      if (!appContainer) {
        throw new Error(`Widget container not found on the page: ${JSON.stringify(widgetInfo)}`);
      }
      mediaQueryContainer = appContainer.parentElement;
      spartaStoreUtility.addData("applicationContext", widgetInfo, true, widgetInfo);
    } else {
      // Sparta page loaded through Wealth Sparta Page Handler which injects mlGlobalData object containing csrfToken
      if (sparta.mlGlobalData) {
        spartaStoreUtility.addData("csrfToken", sparta.mlGlobalData.csrfToken);
      }
    }
    spartaEventsUtility.pub(spartaEventsUtility.EVENTS.APP_CONTEXT_LOADED, spartaStoreUtility.getData("applicationContext", widgetInfo), widgetInfo);
    if (!appContainer) {
      // only for Sparta sites
      let topContainer = document.querySelectorAll("[data-sparta-container]");
      mediaQueryContainer = document.querySelector("[data-sparta-wrapper]");
      if (!topContainer.length) {
        throw new Error("Unable to find a sparta container on the page");
      }
      topContainer = [...topContainer].filter(el => !el.classList.contains("sparta-widget-container"));
      if (topContainer.length !== 1) {
        throw new Error("too many containers on page, must use widget configuration or loader");
      }
      [appContainer] = topContainer;
    }
    this.appContainer = appContainer;
    spartaMediaQueryUtility.init(mediaQueryContainer || appContainer);
    spartaStoreUtility.addData("applicationContainer", appContainer, true, widgetInfo);
    spartaEventsUtility.pub(spartaEventsUtility.EVENTS.APP_CONTAINER_LOADED, appContainer, widgetInfo);
  }

  /**
   * Loads application options/overrides into store from
   * appContainer.attr('data-sparta-overrides')
   */
  loadOptionsFromContainer(widgetInfo) {
    const appContainer = spartaStoreUtility.getData("applicationContainer", widgetInfo);
    const containers = [].slice.call(Array.isArray(appContainer) ? appContainer : [appContainer]);
    const optionsObj = JSON.parse(containers[containers.length - 1].getAttribute("data-sparta-options")) || {};
    spartaStoreUtility.addData("applicationOptions", optionsObj, true, widgetInfo);
  }

  /**
   * Checks the application container if there is a language override.
   */
  checkForLangOverride(widgetInfo) {
    const containers = spartaStoreUtility.getData("applicationContainer", widgetInfo);
    const appContainer = [].slice.call(Array.isArray(containers) ? containers : [containers]);
    const langOverride = appContainer && appContainer[appContainer.length - 1].getAttribute("data-sparta-lang") || null;
    if (langOverride) {
      spartaStoreUtility.addData("lang", langOverride.toLowerCase());
    }
  }

  /**
   * instantiate page modules
   */
  async createModuleInstances(widgetInfo, failedModules = []) {
    const containers = spartaStoreUtility.getData("applicationContainer", widgetInfo);
    const applicationContainer = Array.isArray(containers) ? containers[containers.length - 1] : containers;
    const boaModules = this._getUninitializedModules([...applicationContainer.querySelectorAll('[data-component="module"]')]);

    // Added temporary fix to prevent widget modules to corrupt non widget modules rendering.
    // TODO: Revists once again after adding wiget modules identification from Sparta platform
    // and remove it if widgetInfo is empty.
    if (_.isEmpty(widgetInfo)) {
      const widgetModules = applicationContainer.querySelectorAll('.sparta-widget-container [data-component="module"]');
      _.remove(boaModules, function (boaModule) {
        return _.includes(widgetModules, boaModule);
      });
    }
    failedModules.forEach(moduleRenderData => {
      try {
        const {
          module
        } = moduleRenderData;
        const className = module.config.data.init;
        const moduleDom = document.querySelector(`[data-target-id=${module.config.webContentTarget}]`) || document.getElementById(module.config.id);
        const {
          moduleClass
        } = this.modulesLoaded[className];
        const options = JSON.parse(moduleDom.getAttribute("data-options")) || {};
        options.doNotRegister = true;
        let moduleUtilitiyClass = new moduleClass(options);
        const errors = Object.values(module.dataSources).filter(data => data.errorMessage);
        moduleUtilitiyClass.onError(errors);
      } catch (error) {
        console.error('sparta-bootstrap-utility: Could not trigger module onError', moduleRenderData?.module?.config?.webContentTarget, error);
      }
    });
    for (const moduleDom of boaModules) {
      const className = moduleDom.getAttribute("data-init");
      if (className) {
        // set default options to empty object if no options were provided
        const options = JSON.parse(moduleDom.getAttribute("data-options")) || {};
        const {
          moduleClass,
          moduleConfig
        } = this.modulesLoaded[className];
        const {
          stores = [],
          type = null
        } = moduleConfig;
        // Load and register any store dependencies
        if (stores.length > 0) {
          const store = await this.getVuexStore();
          options.vuex = await this.getVuex();
          options.vuexStore = store;
          for (const storeEntry of stores) {
            const {
              namespace,
              loader
            } = storeEntry;
            if (store.hasModule(namespace) === false) {
              const loadedStore = await loader();
              // Register store module and ensure it is namespaced
              store.registerModule(namespace, {
                ...loadedStore.default,
                namespaced: true
              });
            }
          }
        }

        // pass module Node reference as target to module initialization
        options.target = moduleDom;

        // create module instance
        if (!this.moduleInstances[className]) {
          this.moduleInstances[className] = [];
        }
        if (moduleClass !== undefined) {
          options.widgetInfo = widgetInfo;

          // Add Vue, Vuex & VuexStore instances to each Vue module created
          if (type === 'vue') {
            [options.vue, options.vuex, options.vuexStore] = await Promise.all([this.getVue(), this.getVuex(), this.getVuexStore()]);
          }
          let moduleUtilitiyClass = new moduleClass(options);
          this.moduleInstances[className].push(moduleUtilitiyClass);

          // we need to do this better, but goal is to remove this class after module initted
          // TODO maybe fire a custom event to hook into
          moduleDom.classList.remove("spa-module-init");
        } else {
          console.log("DID NOT FIND MODULE INSTANCE", className, this.moduleInstances);
        }
      } else {
        console.log("className not found in DOM element", moduleDom);
      }
    }
    spartaStoreUtility.addData("moduleInstances", this.moduleInstances);
  }

  /**
   * Verify if there are modules with duplicate ids, rename the ids to make them unique
   * @param {object} module
   */
  verifyUniqueModuleIds(module) {
    if (!module.settings.target || !module.settings.target.id) {
      return;
    }
    const moduleContainers = document.querySelectorAll(`[id="${module.settings.target.id}"]`);
    if (moduleContainers && moduleContainers.length > 1) {
      let imSettings,
        mSettings = module.settings;
      this.initedModules.reverse().every(im => {
        imSettings = im.module.settings;
        if (imSettings.moduleInfo && imSettings.moduleInfo.name === mSettings.moduleInfo.name && imSettings.target.id.startsWith(mSettings.target.id)) {
          // increment the total instances
          mSettings.target.totalInstances = (imSettings.target.totalInstances || 0) + 1;

          // keep the totalInstances only with the last instance
          delete imSettings.target.totalInstances;

          // rename the module container id
          mSettings.target.id = mSettings.target.id + mSettings.target.totalInstances;
          return false;
        }
        return true;
      });
    }
  }
  getExportName(module) {
    const exportName = module._spartaComponentConfig.export;
    if (typeof exportName === "string") {
      return exportName;
    }
    return exportName[0];
  }

  /**
   * call init functions of all instantiated page modules
   */
  async initModules() {
    console.log("### initModules");
    const dynamicImport = this.dynamicImport || this.spaParams.dynamicImportMap;
    for (const module of this.modules) {
      // this.modules.forEach(module => {
      if (module.state === "ready") {
        this.verifyUniqueModuleIds(module.module);
        if (this.routerInstance) {
          module.module.router = this.routerInstance;
        }
        if (this.vueVersion === 3) {
          const vueRouterImport = _.find(dynamicImport.modules, module => module.name === '@sparta.global.vendors/vue-router');
          if (vueRouterImport) {
            // Vue Router needed before init to set history config option for VueRouter
            module.module.VueRouter = this.VueRouter || (await vueRouterImport.loader());
          }
        }
        const componentDefinition = module.module.getVueComponentDefintion || module.module.componentDefinition;
        if (!componentDefinition) {
          await module.module[module.init](module.args);
        }
        module.state = "initialized";
        this.initedModules.push(module);
        if (componentDefinition) {
          const vueComponentDefinition = componentDefinition.bind(module.module);
          if (vueComponentDefinition) {
            const vueModulePlaceholder = document.createElement("div");
            module.module.settings.target.appendChild(vueModulePlaceholder);
            const {
              routerConfiguration,
              ...vueComponentDefDetail
            } = vueComponentDefinition();
            let router;
            let routerDetail;
            if (routerConfiguration) {
              if (vueComponentDefDetail.render || vueComponentDefDetail.template) {
                const propType = vueComponentDefDetail.render ? 'Render' : 'Template';
                console.warn(`${propType} property found in ${module.module.constructor.name} componentDefinition will be ignored.`);
              }
              this.hasRouterConfig = true;
              if (this.vueVersion === 3) {
                this.VueRouter = module.module.VueRouter;
                router = this.VueRouter.createRouter(routerConfiguration);
              } else {
                const vueRouterImport = _.find(dynamicImport.modules, module => module.name === '@sparta.global.vendors/vue-router');
                if (vueRouterImport) {
                  this.VueRouter = this.VueRouter || (await vueRouterImport.loader()).default;
                  this.vue.use(this.VueRouter);
                  router = new this.VueRouter(routerConfiguration);
                }
              }
              if (this.vuexStore) {
                this.vuexStore.$router = router;
              }

              // update meta title for routes
              router.beforeEach((to, from, next) => {
                if (to.meta && to.meta.title) {
                  document.title = to.meta.title;
                }
                next();
              });
              const {
                h,
                resolveComponent
              } = this.vue;
              routerDetail = {
                router,
                render: this.vueVersion === 2 ? function (h) {
                  return h('router-view');
                } : function () {
                  return h(resolveComponent('router-view'));
                }
              };
            }
            const mergedComponentDefinition = {
              ...vueComponentDefDetail,
              ...routerDetail
            };
            const vuexStore = await this.getVuexStore();
            module.module.vueComponent = this.vueVersion === 3 ? await this.createAndMountApp(mergedComponentDefinition, vueModulePlaceholder, router) : new this.vue({
              el: vueModulePlaceholder,
              store: vuexStore,
              ...mergedComponentDefinition
            });
            if (this.spaParams.isSPA && !routerConfiguration) {
              if (this.vuexStore) {
                this.vuexStore.$router = this.routerInstance;
              }
              module.module.vueComponent.router = this.routerInstance;
            }
          }
        }
      }
    }
    // });
  }
  registerModuleClass({
    moduleClass,
    moduleConfig
  }) {
    if (!this.modulesLoaded[moduleConfig.data.init]) {
      this.modulesLoaded[moduleConfig.data.init] = {
        moduleClass,
        moduleConfig
      };
    }
  }

  /**
   * called by modules super
   * @param {object} module
   * @param {object} args
   * @param {string} init - name of function to be called in initModules
   */
  register(module, args = {}, init = "init") {
    this.modules.push({
      state: "ready",
      module,
      init,
      args
    });
  }

  /**
   * trigger throttled spartaWindowScroll event
   */
  setupGlobalEvents(widgetInfo) {
    // Scroll
    window.addEventListener("scroll", _.throttle(e => {
      spartaEventsUtility.pub(this.EVENTS.SPARTA_WINDOW_SCROLL, [window.pageYOffset, e], widgetInfo);
    }, 20));
  }

  /**
   * run onloadHelpers for .spa-fn elements
   * @param {*} widgetInfo Object for the module widget
   * @returns {void}
   */
  onloadHelpers(widgetInfo) {
    const containers = spartaStoreUtility.getData("applicationContainer", widgetInfo);
    const applicationContainer = Array.isArray(containers) ? containers[containers.length - 1] : containers;

    // Find all elements with a `spa-fn` class to run functions on
    const spaFnElems = this._getUninitializedModules([...applicationContainer.querySelectorAll('.spa-fn')]);
    if (!spaFnElems.length) {
      return;
    }
    const onloadHelperRegister = ['.spa-boa-window', '.spa-mobile-tel'];
    onloadHelperRegister.forEach(helperClass => {
      if (helperClass === '.spa-mobile-tel' && !this.browser.mobile) {
        return;
      }
      const elsToProcess = this._getUninitializedModules(applicationContainer.querySelectorAll(`.spa-fn${helperClass}`));
      if (elsToProcess.length) {
        switch (helperClass) {
          case '.spa-boa-window':
            spartaUILayersUtility.addPopupWindowHelper(elsToProcess);
            break;
          case '.spa-mobile-tel':
            spartaGlobalFunctions.mobilePhoneFormatHelper(elsToProcess);
            break;
        }
      }
    }, this);
  }

  /**
   * Helper fn that, given a list of modules, only returns uninitialized modules
   */
  _getUninitializedModules(modules) {
    return _.filter(modules, module => !_.find(this.initedModules, initedModule => initedModule.module.settings.target === module));
  }
  initializeRouter(widgetInfo) {
    const spaAssetsHref = document.querySelector("[href*=spa-assets]").href;
    const spaMapUrl = `/${spaAssetsHref.substring(0, spaAssetsHref.indexOf("/spa-assets/") + 12).replace(/^[a-zA-Z]{3,5}:\/{2}[a-zA-Z0-9_.:-]+\//, "")}${this.spaParams.spaFileName}`;
    return new Promise((resolve, reject) => {
      spartaTransportUtility.subscribe({
        cache: true,
        dataType: "json",
        success: data => {
          resolve(data);
        },
        error: () => {
          // Still resolve so that we can use any
          // default data sources, if applicable.
          reject();
        },
        url: spaMapUrl
      });
    }).then(spaMap => {
      this.spaMap = spaMap;
      const altLanguageRoutes = this.getAltLanguageRoutes(spaMap.routes);
      spaMap.routes = [...spaMap.routes, ...altLanguageRoutes];
      this.routeModulesContainer = this.routeModulesContainer || document.querySelector('#dynamic-row-body> .row > .column');
      const renderOptions = {
        style: {
          display: 'none'
        },
        domProps: {
          innerHTML: Math.random()
        }
      };
      const routeComponent = this.vueVersion === 2 ? async (resolve, reject) => {
        try {
          return resolve({
            render: h => h('div', renderOptions)
          });
        } catch (error) {
          return reject(error);
        }
      } : () => new Promise((resolve, reject) => {
        try {
          const {
            h
          } = this.vue;
          return resolve({
            render: () => h('div', renderOptions)
          });
        } catch (error) {
          return reject(error);
        }
      });
      const routes = _.map(spaMap.routes, (route, i) => ({
        name: route.name,
        path: route.path,
        title: route.title,
        meta: {
          routeIndex: i
        },
        component: routeComponent
      }));
      this.routerInstance = this.vueVersion === 3 ? this.VueRouter.createRouter({
        routes,
        history: this.VueRouter.createWebHistory()
      }) : new this.VueRouter({
        routes,
        mode: 'history'
      });
      this.initedModules.forEach(module => {
        module.module.router = this.routerInstance;
      });
      const vueRoutePlaceholder = document.createElement("div");
      vueRoutePlaceholder.id = "routerVueInstanceContainer";
      document.body.appendChild(vueRoutePlaceholder);
      const self = this;

      // Vue 3 router will have a "from" route of "/" on first load.
      // Ignoring the route watcher triggered on page load which causes multi initialization issue.
      let initialLoad = self.vueVersion === 3;

      // create dummy vue instance to react to route changes
      const routeComponentInfo = {
        el: vueRoutePlaceholder,
        router: this.routerInstance,
        render: self.vueVersion === 2 ? function (h) {
          return h('router-view');
        } : function () {
          return self.vue.h(self.vue.resolveComponent('router-view'));
        },
        watch: {
          $route(to, from) {
            try {
              if (from && to && from !== to && self.spaMap.routes[to.meta.routeIndex]) {
                self.spaParams.pageParams = self.spaMap.routes[to.meta.routeIndex].pageDetails.pageParams;
                if (!initialLoad) {
                  self.clearRouteModules(self.spaMap.routes[from.meta.routeIndex]);
                  self.loadRouteModules(self.spaMap.routes[to.meta.routeIndex], widgetInfo);
                }
              }
              initialLoad = false;
            } catch (error) {
              console.warn(`Error for path ${to.path} at ${error.stack}.`);
            }
          }
        }
      };
      if (this.vueVersion === 3) {
        this.createAndMountApp(routeComponentInfo, vueRoutePlaceholder);
      } else {
        new this.vue(routeComponentInfo);
      }
    });
  }
  getAltLanguageRoutes(routes) {
    const altLangRoutes = [];
    _.each(routes, route => {
      route.language = "en";
      // check for languages other than English
      const altLanguages = [...route.pageDetails.pageParams.languages.languages].filter(lang => lang !== "en");
      if (altLanguages) {
        // create new route for each alternative language
        _.each(altLanguages, language => {
          altLangRoutes.push({
            path: `${route.path.replace(/\/$/, "")}/${language}`,
            name: `${route.name}-${language}`,
            pageDetails: route.pageDetails,
            modules: route.modules,
            language
          });
        });
      }
    });
    return altLangRoutes;
  }
  async loadRouteModules(route, widgetInfo) {
    this.spaParams.language = window.sparta.language = route.language;
    if (route.title) {
      document.title = route.title;
    }
    spartaStoreUtility.addData("spaParams", this.spaParams);
    spartaEventsUtility.pub(spartaEventsUtility.EVENTS.SPA_PARAMS_LOADED, this.spaParams);
    const refModule = this.initedModules[0].module;
    await refModule.mountAll(_.map(route.modules, mod => ({
      ...mod,
      module: mod.name,
      target: "#dynamic-row-body> .row > .column"
    })));
  }
  clearRouteModules(route) {
    const initedModules = [];
    let clearModule = true;
    this.initedModules.forEach(m => {
      clearModule = false;
      if (route) {
        route.modules.forEach(module => {
          if (module.name === m.module.settings.config.name) {
            clearModule = true;
          }
        });
      }
      if (!clearModule) {
        initedModules.push(m);
      } else {
        // if it is vue module then destroy the vue instance
        if (m.module.vueComponent) {
          if (this.vueVersion === 3) {
            m.module.vueComponent.unmount();
          } else {
            m.module.vueComponent.$destroy();
          }
        }
        // call modules destroy method
        if (m.module.destroy) {
          m.module.destroy();
        }
      }
    });
    this.initedModules = initedModules;
    //this.modulesLoaded = [];
    while (this.routeModulesContainer && this.routeModulesContainer.firstChild) {
      this.routeModulesContainer.removeChild(this.routeModulesContainer.firstChild);
    }
  }

  /**
   * Create and initialize modules
   */
  async createAndInitModules(widgetInfo, failedModules = []) {
    // Initialize page modules
    await this.createModuleInstances(widgetInfo, failedModules);
    await this.onloadHelpers(widgetInfo);
    await this.initModules();
  }

  /**
   * Initializes the CMS utility
   * Both site and page params can be set to enable/disable this utility
   */
  initContentManagerUtility(widgetInfo) {
    const getSpaParams = spartaStoreUtility.getData('spaParams');
    let autoRunCmsUtility = true;

    // Check site settings
    autoRunCmsUtility = 'autoRunCmsUtility' in getSpaParams.siteParams ? getSpaParams.siteParams.autoRunCmsUtility : autoRunCmsUtility;

    // Check page params
    autoRunCmsUtility = 'autoRunCmsUtility' in getSpaParams.pageParams ? getSpaParams.pageParams.autoRunCmsUtility : autoRunCmsUtility;
    if (autoRunCmsUtility) {
      spartaContentManagerUtility.init({
        widgetInfo
      });
    }
  }

  /**
   * CSP: Add common click event listener to support the removal of 'javascript:void(0)' from links
   * @param {object} el - optional - element of on demand module so the entire dom is not targeted to add event listener when an element is passed
   */
  addPreventDefaultForLinks(el) {
    if (el instanceof Element || el instanceof Document) {
      const cspHelperLinks = [...el.querySelectorAll('a.spa-prevent-default-click')];
      cspHelperLinks.forEach(link => {
        link.addEventListener('click', e => {
          e.preventDefault();
        });
      });
    }
  }

  /**
   * sets application data for storybook components
   * @returns {void}
   */
  setApplicationDataForStorybook(appContainer) {
    this.appContainer = appContainer;
    spartaMediaQueryUtility.init(this.appContainer);
    spartaStoreUtility.addData("applicationContainer", this.appContainer, true);
    spartaEventsUtility.pub(spartaEventsUtility.EVENTS.APP_CONTAINER_LOADED, this.appContainer);
  }

  /**
   * function to initialize storybook with required bootstrap logic
   * @returns {void}
   */
  initStorybook(appContainer) {
    this.setupSpaParams();
    this.setApplicationDataForStorybook(appContainer);
    this.setupGlobalEvents();
    publishGlobalReadyEvents(this.loop, this.EVENTS);
  }
  async mountAll(modules, widgetInfo, renderRuntimeModules = false) {
    await mountAll.call(this, modules, widgetInfo, renderRuntimeModules);
  }
}
var index = new SpartaBootstrapUtility();

export { index as default };
